import { preciseNumber } from "../../store/reducers/cart";
import { getProductInventory } from "../../store/reducers/store";
import StockWarningMessage from "../ProductDetails/StockWarningMessage";
import React from "react";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
dayjs.extend(duration);

let isOutOfStock = (product, cart, usedComponents) => {
  if (!product) return false;
  if (
    !product.has_variants &&
    ((product.max_addable_quantity !== null &&
      product.max_addable_quantity <= 0) ||
      (["stocked", "composite"].includes(product.type_of_product) &&
        getProductInventory(product, usedComponents) <
          product.min_addable_quantity &&
        (!["stocked", "composite"].includes(product.type_of_product) ||
          !product.allow_preordering ||
          product.preorder_inventory <= 0)) ||
      (product.type_of_product === "bookable" &&
        !product.booking_slots.some(
          (bookingSlot) => bookingSlot.inventory > 0
        ) &&
        !(!product.booking_slots.length && product.has_booking_slots))) &&
    !cart.some((p) => p.id === product.id)
  ) {
    return true;
  }

  return false;
};

let getOriginalProduct = (product, category_id, categories) => {
  let store_product = product;

  if (category_id !== product.category_id) {
    try {
      store_product = categories.find(
        (category) => category.id === product.category_id
      );

      if (product.parent) {
        store_product = store_product.products
          .find((parent) => parent.id === product.parent)
          .children.find((p) => p.id === product.id);
      } else {
        store_product = store_product.products.find((p) => p.id === product.id);
      }
    } catch (err) {
      store_product = product;
    }
  }

  return store_product || product;
};

const getPrepTime = (prep, language) => {
  let prepTime = "";

  const d = dayjs.duration(prep, "minutes");

  const days = d.days();
  const hours = d.hours();
  const minutes = d.minutes();
  const seconds = d.seconds();

  if (language === "english") {
    if (days) prepTime += ` ${days}d`;
    if (hours) prepTime += ` ${hours}h`;
    if (minutes) prepTime += ` ${minutes}m`;
    if (seconds) prepTime += ` ${seconds}s`;
  } else {
    if (days) prepTime += ` ${days}يوم`;
    if (hours) prepTime += ` ${hours}س`;
    if (minutes) prepTime += ` ${minutes}د`;
    if (seconds) prepTime += ` ${seconds}ث`;
  }
  return prepTime.trim();
};

let getMaximumProductInventoryFromModifiers = (
  options,
  currentQuantity,
  usedComponents,
  editedProduct,
  productUsedComponents,
  currentProduct
) => {
  let productMaxInventory = Infinity;
  const totalUsedComponents = { ...usedComponents };
  Object.entries(editedProduct?.prevUsedComponents || {}).forEach(
    ([optionId, optionQuantity]) => {
      const newOptionId = optionId.toString().replace("Q", "");
      totalUsedComponents[newOptionId] =
        (totalUsedComponents[newOptionId] || 0) -
        optionQuantity *
          ((optionId.toString().includes("Q") && 1) || editedProduct.quantity);
    }
  );

  Object.entries(productUsedComponents || {}).forEach(
    ([optionId, optionQuantity]) => {
      const newOptionId = optionId.toString().replace("Q", "");
      totalUsedComponents[newOptionId] =
        (totalUsedComponents[newOptionId] || 0) +
        optionQuantity *
          ((optionId.toString().includes("Q") && 1) || currentQuantity);
    }
  );

  Object.values(options).forEach((option) => {
    Object.values(option.selectedOptions).forEach((choice) => {
      if (
        choice.maxInventory !== null &&
        choice.related_product &&
        !option.quantityExclusive
      ) {
        let choiceInventory = getProductInventory(
          choice.related_product,
          totalUsedComponents
        );

        const maxExtraAmount =
          choiceInventory /
            (choice.quantity +
              getQuantityUsedPerProductIncrease(
                choice.related_product,
                currentProduct
              )) +
          currentQuantity;

        productMaxInventory = Math.min(productMaxInventory, maxExtraAmount);
        if (choice.related_product.type_of_product === "stocked") {
          totalUsedComponents[choice.related_product.id] =
            (totalUsedComponents[choice.related_product.id] || 0) +
            currentQuantity * choice.quantity;
        } else if (choice.related_product.type_of_product === "composite") {
          Object.entries(choice.related_product.components).forEach(
            ([productId, product]) => {
              totalUsedComponents[productId] =
                (totalUsedComponents[productId] || 0) +
                currentQuantity * choice.quantity * product.required_amount;
            }
          );
        }
      }
    });
  });
  return productMaxInventory;
};
let getQuantityUsedPerProductIncrease = (modifier, currentProduct) => {
  const componentsToBeUsed = {};
  if (currentProduct.type_of_product === "composite") {
    Object.entries(currentProduct.components || {}).forEach(
      ([productId, { required_amount }]) => {
        componentsToBeUsed[productId] =
          (componentsToBeUsed[productId] || 0) + required_amount;
      }
    );
  } else if (currentProduct.type_of_product === "stocked") {
    componentsToBeUsed[currentProduct.id] =
      componentsToBeUsed[currentProduct.id] || 0;
  }
  return (
    modifier.inventory_on_hand -
    getProductInventory(modifier, componentsToBeUsed)
  );
};

let getParentCategories = (
  category,
  categories,
  nonMingledParentCategory = null,
  parentCategories = []
) => {
  const parent = categories.find((cat) =>
    cat.sub_categories.includes(category)
  );
  if (parent) {
    parentCategories.push(parent.id);
    if (parent.no_mingling) nonMingledParentCategory = parent.id;
    if (parent.is_child)
      return getParentCategories(
        parent.id,
        categories,
        nonMingledParentCategory,
        parentCategories
      );
  }
  return { nonMingledParentCategory, parentCategories };
};
const sortSmartSearchResult = (a, b, searchValue) => {
  const aName = a.name.toLowerCase();
  const aNameAr = a.ar_name.toLowerCase();
  const bName = b.name.toLowerCase();
  const bNameAr = b.ar_name.toLowerCase();
  const aDescription = a.description.toLowerCase();
  const aDescriptionAr = a.ar_description.toLowerCase();
  const bDescription = b.description.toLowerCase();
  const bDescriptionAr = b.ar_description.toLowerCase();
  const aShortDescription = a.short_description.toLowerCase();
  const aShortDescriptionAr = a.ar_short_description.toLowerCase();
  const bShortDescription = b.short_description.toLowerCase();
  const bShortDescriptionAr = b.ar_short_description.toLowerCase();
  const filterValue = searchValue.toLowerCase();

  const aNameIndex =
    aName.indexOf(filterValue) !== -1
      ? aName.indexOf(filterValue)
      : aNameAr.indexOf(filterValue);
  const bNameIndex =
    bName.indexOf(filterValue) !== -1
      ? bName.indexOf(filterValue)
      : bNameAr.indexOf(filterValue);
  const aDescriptionIndex =
    aDescription.indexOf(filterValue) !== -1
      ? aDescription.indexOf(filterValue)
      : aDescriptionAr.indexOf(filterValue);
  const bDescriptionIndex =
    bDescription.indexOf(filterValue) !== -1
      ? bDescription.indexOf(filterValue)
      : bDescriptionAr.indexOf(filterValue);
  const aShortDescriptionIndex =
    aShortDescription.indexOf(filterValue) !== -1
      ? aShortDescription.indexOf(filterValue)
      : aShortDescriptionAr.indexOf(filterValue);
  const bShortDescriptionIndex =
    bShortDescription.indexOf(filterValue) !== -1
      ? bShortDescription.indexOf(filterValue)
      : bShortDescriptionAr.indexOf(filterValue);

  if (aNameIndex === -1 && bNameIndex === -1) {
    if (aShortDescriptionIndex === -1 && bShortDescriptionIndex === -1) {
      if (aDescriptionIndex === -1 && bDescriptionIndex === -1) {
        return 0;
      } else if (aDescriptionIndex === -1) {
        return 1;
      } else if (bDescriptionIndex === -1) {
        return -1;
      } else {
        return aDescriptionIndex - bDescriptionIndex;
      }
    } else if (aShortDescriptionIndex === -1) {
      return 1;
    } else if (bShortDescriptionIndex === -1) {
      return -1;
    } else {
      return aShortDescriptionIndex - bShortDescriptionIndex;
    }
  } else if (aNameIndex === -1) {
    return 1;
  } else if (bNameIndex === -1) {
    return -1;
  } else {
    return aNameIndex - bNameIndex;
  }
};

const checkForStockWarningMessage = (
  show_warning_item_availability,
  inventory_on_hand,
  warning_item_availability_count,
  outOfStock,
  warning_item_availability_message_value,
  langauge,
  has_variants,
  type_of_product
) => {
  if (
    type_of_product === "stocked" &&
    show_warning_item_availability &&
    !outOfStock &&
    inventory_on_hand <= warning_item_availability_count &&
    !has_variants
  ) {
    return (
      <StockWarningMessage
        messageValue={warning_item_availability_message_value}
        inventoryOnHand={inventory_on_hand}
        language={langauge}
      />
    );
  } else {
    return false;
  }
};

const setFromQuantityBox = (modalQuantity, currentQuantity, props) => {
  const {
    current_product,
    preorderingMode,
    selectedOptions,
    usedComponents,
    productUsedComponents,
    editedProduct,
    booking_slot,
  } = props;
  let maxInventory = Infinity;
  if (["stocked", "composite"].includes(current_product.type_of_product)) {
    if (preorderingMode) {
      maxInventory =
        current_product.preorder_inventory + (editedProduct?.quantity || 0);
    } else {
      maxInventory =
        getProductInventory(current_product, usedComponents) +
        (editedProduct?.quantity || 0);
    }
  } else if (current_product.type_of_product === "bookable") {
    maxInventory =
      current_product.booking_slots.find((slot) => slot.id === booking_slot)
        ?.inventory +
      ((editedProduct?.booking_slot === booking_slot &&
        editedProduct?.quantity) ||
        0);
  }

  let newQuantity = Math.max(
    Math.min(
      9999999,
      Number(modalQuantity),
      maxInventory,
      current_product.max_addable_quantity || Infinity,
      getMaximumProductInventoryFromModifiers(
        selectedOptions,
        currentQuantity,
        usedComponents,
        editedProduct,
        productUsedComponents,
        current_product
      )
    ),
    current_product.min_addable_quantity
  );

  if (newQuantity % current_product.increments !== 0) {
    newQuantity =
      current_product.increments *
      parseInt(newQuantity / current_product.increments);
  }
  return changeQuantity(
    Number(newQuantity - currentQuantity),
    currentQuantity,
    props
  );
};

const changeQuantity = (i, currentQuantity, props) => {
  const {
    current_product,
    preorderingMode,
    usedComponents,
    productUsedComponents,
    editedProduct,

    promo_items_addable_quantity,
    selectedOptions,
    booking_slot,
  } = props;
  let newQuantity = preciseNumber(currentQuantity + i);
  const isAddingQuantity = newQuantity > currentQuantity;
  const includesPromo =
    promo_items_addable_quantity && promo_items_addable_quantity > 0;
  if (includesPromo) {
    if (isAddingQuantity && promo_items_addable_quantity === currentQuantity) {
      newQuantity = getNewLeapQuantity(
        current_product,
        currentQuantity,
        isAddingQuantity,
        booking_slot,
        promo_items_addable_quantity,
        usedComponents
      );
    } else if (
      !isAddingQuantity &&
      newQuantity > promo_items_addable_quantity &&
      newQuantity - promo_items_addable_quantity <
        current_product.min_addable_quantity
    ) {
      newQuantity = getNewLeapQuantity(
        current_product,
        currentQuantity,
        isAddingQuantity,
        booking_slot,
        promo_items_addable_quantity,
        usedComponents
      );
    }
  }
  const totalUsedComponents = { ...usedComponents };

  if (["stocked", "composite"].includes(current_product.type_of_product)) {
    Object.entries(editedProduct?.prevUsedComponents || {}).forEach(
      ([optionId, optionQuantity]) => {
        const newOptionId = optionId.toString().replace("Q", "");
        totalUsedComponents[newOptionId] =
          (totalUsedComponents[newOptionId] || 0) -
          optionQuantity *
            ((optionId.toString().includes("Q") && 1) ||
              editedProduct.quantity);
      }
    );
    Object.entries(productUsedComponents || {}).forEach(
      ([optionId, optionQuantity]) => {
        const newOptionId = optionId.toString().replace("Q", "");
        totalUsedComponents[newOptionId] =
          (totalUsedComponents[newOptionId] || 0) +
          optionQuantity *
            ((optionId.toString().includes("Q") && 1) || currentQuantity);
      }
    );
  }

  if (
    newQuantity > 0 &&
    checkMinMaxOrPromo(current_product, newQuantity, includesPromo, props) &&
    ((preorderingMode &&
      newQuantity <=
        current_product.preorder_inventory + (editedProduct?.quantity || 0)) ||
      (!preorderingMode &&
        ((["stocked", "composite"].includes(current_product.type_of_product) &&
          newQuantity <=
            getProductInventory(current_product, totalUsedComponents) +
              currentQuantity) ||
          current_product.type_of_product === "produced" ||
          (current_product.type_of_product === "bookable" &&
            booking_slot !== -1 &&
            newQuantity <=
              current_product.booking_slots.find(
                (slot) => slot.id === booking_slot
              ).inventory +
                ((editedProduct?.booking_slot === booking_slot &&
                  editedProduct?.quantity) ||
                  0))))) &&
    (!isAddingQuantity ||
      newQuantity <=
        getMaximumProductInventoryFromModifiers(
          selectedOptions,
          i,
          usedComponents,
          editedProduct,
          productUsedComponents,
          current_product
        ))
  ) {
    return newQuantity;
  }
  return null;
};

const getNewLeapQuantity = (
  current_product,
  currentQuantity,
  isAddingQuantity,
  booking_slot,
  promo_items_addable_quantity,
  usedComponents
) => {
  if (isAddingQuantity) {
    switch (current_product.type_of_product) {
      case "composite":
      case "stocked":
        return Math.min(
          currentQuantity + current_product.min_addable_quantity,
          getProductInventory(current_product, usedComponents)
        );

      case "bookable":
        if (booking_slot !== -1) {
          const bookingSlot = current_product.booking_slots.find(
            (slot) => slot.id === booking_slot
          );
          if (!bookingSlot) return currentQuantity;
          return Math.min(
            currentQuantity + current_product.min_addable_quantity,
            bookingSlot.inventory
          );
        } else {
          return currentQuantity;
        }

      default:
        return currentQuantity + current_product.min_addable_quantity;
    }
  } else {
    return promo_items_addable_quantity;
  }
};

const checkMinMaxOrPromo = (
  current_product,
  newQuantity,
  includesPromo,
  props
) => {
  let promo_items_addable_quantity = 0;
  if (includesPromo) {
    promo_items_addable_quantity = props.promo_items_addable_quantity;
  }

  if (
    (current_product.max_addable_quantity === null ||
      newQuantity <=
        current_product.max_addable_quantity +
          promo_items_addable_quantity -
          props.addedQuantity) &&
    (includesPromo || newQuantity >= current_product.min_addable_quantity)
  ) {
    return true;
  }
  return false;
};
let getProductPrice = (product, bookingSlotId) => {
  let unitPrice = product.price;
  if (
    bookingSlotId &&
    bookingSlotId !== -1 &&
    product.type_of_product == "bookable" &&
    product.booking_slots?.length > 0
  ) {
    const slot = product.booking_slots.find((s) => s.id === bookingSlotId);

    if (slot && slot?.price != undefined) {
      unitPrice = slot.price;
    }
  }
  return unitPrice;
};

export {
  isOutOfStock,
  getOriginalProduct,
  getPrepTime,
  getParentCategories,
  getMaximumProductInventoryFromModifiers,
  checkForStockWarningMessage,
  sortSmartSearchResult,
  setFromQuantityBox,
  changeQuantity,
  getProductPrice,
};
